import React, { useState, useEffect } from 'react';
import { Link, Redirect } from 'react-router-dom';

import { connect } from 'react-redux';
import logo from '../assets/imgs/DigitalContacts-logo.svg';
import {
  matchCode,
  sendCodeToEmail,
  openResetLink,
} from '../actions/resetPassword';
import {
  rememberResetLink,
  recallResetLink,
} from '../utils/resetLinkSession';

// The emailed "Verify and reset my password" button lands here with the
// address and code in the URL *fragment* -- #e=<email>&c=<code>. A
// fragment is never sent to the server, so neither value reaches Heroku's
// router logs, a Referer header, or any proxy access log along the way.
//
// Read once, synchronously, and then removed from the URL entirely (see
// stripResetLink below). Returns empty strings rather than null so the
// callers below can treat "absent" and "blank" identically.
const parseResetFragment = () => {
  if (typeof window === 'undefined') return { email: '', code: '' };
  const hash = window.location.hash || '';
  if (!hash.startsWith('#')) return { email: '', code: '' };
  try {
    const params = new URLSearchParams(hash.slice(1));
    return { email: params.get('e') || '', code: params.get('c') || '' };
  } catch (err) {
    return { email: '', code: '' };
  }
};

// The fragment if it is there, otherwise whatever this tab remembered
// from a previous load.
//
// The fallback is not belt-and-braces, it is load-bearing. On a cold
// arrival -- a browser that has never opened the site, which is precisely
// what an emailed link produces -- the service worker installs, claims
// the client and reloads the page (see client/src/serviceWorker.js's
// 'controllerchange' listener). That happens *after* this screen has
// already stripped the fragment, so without somewhere durable to put it
// the reload lands on /enter_code with an empty store and an empty URL,
// and the guard below bounces the customer to /register. Which is exactly
// what it did.
const readResetLink = () => {
  const fromFragment = parseResetFragment();
  if (fromFragment.email) {
    rememberResetLink(fromFragment);
    return fromFragment;
  }
  const remembered = recallResetLink();
  return { email: remembered.email, code: remembered.code };
};

// Takes the code and address out of the address bar, the session's
// history entry and anything the customer might bookmark or share. Called
// unconditionally once mounted, including when parsing found nothing, so
// a malformed fragment is not left sitting there either.
//
// replaceState rather than pushState: pushing would leave the original
// URL one Back press away, which is the thing being removed.
const stripResetLink = () => {
  if (typeof window === 'undefined' || !window.history.replaceState) return;
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search
  );
};

// Renders a ms duration as m:ss.
const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const EnterCode = ({
  matchCode,
  sendCodeToEmail,
  openResetLink,
  emailFound,
  error,
  codeMatched,
  user,
  initialCodeExpiresAt,
  initialCanResendAt,
}) => {
  // Lazy initialiser, so the fragment is read during the very first
  // render -- before the emailFound guard below runs. Reading it in an
  // effect instead would be too late: the guard would already have
  // redirected a customer arriving from the email, because Redux is
  // empty on a cold page load.
  const [resetLink] = useState(readResetLink);
  const [code, setcode] = useState(resetLink.code || '');
  const [codeExpiresAt, setCodeExpiresAt] = useState(initialCodeExpiresAt);
  const [canResendAt, setCanResendAt] = useState(initialCanResendAt);
  const [resending, setResending] = useState(false);
  // Ticks once a second so the countdowns below are recomputed from the
  // absolute timestamps rather than decremented, so they stay correct
  // even if the tab was backgrounded and timers got throttled.
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Seed Redux from the link and clear the URL. Runs once. openResetLink
  // dispatches only -- it makes no request, so arriving here neither
  // issues a new code nor spends one of the three requests allowed per
  // two hours, and nothing here can count against the three-strikes
  // lockout: the only thing that touches those counters is submitting
  // the form below.
  useEffect(() => {
    if (resetLink.email) openResetLink(resetLink.email);
    stripResetLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The mount effect above covers the only way a customer actually gets
  // here from the email -- a fresh document load. It does not cover
  // arriving at this screen a second time without one: navigating from
  // /enter_code to /enter_code#e=...&c=... is a same-document fragment
  // change, so the browser never reloads, React never remounts, and a
  // mount effect never runs. The address and code would then sit in the
  // URL exactly as if nothing had been done about it.
  //
  // Unlikely in practice, but "the code is not left in the URL" is the
  // whole point of stripping it, so it should not be conditional on how
  // the customer got here.
  useEffect(() => {
    const onHashChange = () => {
      const link = readResetLink();
      if (link.code) setcode(link.code);
      if (link.email) openResetLink(link.email);
      stripResetLink();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The link carries the address, so a customer arriving from the email
  // has it before Redux does -- the effect above only lands after this
  // first render. Falling back to it keeps the guard from bouncing them
  // to /register on the way in.
  const emailForRequests = user || resetLink.email;

  // No isAuth gate -- see Reset.js. What remains is a flow-integrity
  // check, not an authorisation one: this screen needs an address to
  // verify a code against, which arrives either from the request form
  // (Redux) or from the emailed link's fragment.
  if (!(emailFound || resetLink.email)) {
    return <Redirect to='/register' />;
  }

  if (codeMatched) {
    return <Redirect to='/new_password' />;
  }
  if (error) {
    alert(error);
  }

  const msUntilExpiry = codeExpiresAt
    ? new Date(codeExpiresAt).getTime() - now
    : null;
  const msUntilResend = canResendAt
    ? new Date(canResendAt).getTime() - now
    : null;
  const isExpired = msUntilExpiry !== null && msUntilExpiry <= 0;
  const canResendNow = msUntilResend === null || msUntilResend <= 0;

  const handleResend = async () => {
    setResending(true);
    const result = await sendCodeToEmail(emailForRequests);
    if (result) {
      if (result.expiresAt) setCodeExpiresAt(result.expiresAt);
      if (result.canResendAt) setCanResendAt(result.canResendAt);
    }
    setResending(false);
  };

  return (
    <div class='my-name create-account wel-back'>
      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='login1'>
              <Link to='login' className='account1'>
                Got your card?
              </Link>
              <Link to='login' className='log-in'>
                login
              </Link>
            </div>
            <div className='logo'>
              <Link to='/login'>
                <img src={logo} alt='logo' />
              </Link>
            </div>
            <form
              id='sendOtp'
              onSubmit={(e) => {
                e.preventDefault();
                matchCode(code, emailForRequests);
              }}
            >
              <h1>Enter 6 digit code</h1>
              <input
                type='text'
                name='code'
                id='iEmail'
                placeholder='Code'
                required
                minLength='6'
                maxLength='6'
                value={code}
                onChange={(e) => setcode(e.target.value)}
              />
              {codeExpiresAt && (
                <p style={{ fontSize: '12px', marginTop: '6px' }}>
                  {isExpired
                    ? 'This code has expired. Request a new one below.'
                    : `Code expires in ${formatCountdown(msUntilExpiry)}`}
                </p>
              )}
              <p style={{ fontSize: '12px', marginTop: '2px' }}>
                Didn't get it? Check your spam or junk folder — it comes from
                no-reply@digitalcontacts.info.
              </p>
              <button
                className='btn w-100prc'
                id='submitBtnSendOtp'
                disabled={isExpired || resending}
              >
                Submit
              </button>
              <button
                type='button'
                className='btn w-100prc'
                onClick={handleResend}
                disabled={!canResendNow || resending}
                style={{ marginTop: '10px' }}
              >
                {resending
                  ? 'Sending...'
                  : canResendNow
                  ? 'Resend code'
                  : `Resend in ${formatCountdown(msUntilResend)}`}
              </button>
            </form>

            <Link to='login' className='forgot'>
              Back to Login?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state.registerUser.loading,
  emailFound: state.resetPassword.emailFound,
  error: state.resetPassword.error,
  codeMatched: state.resetPassword.codeMatched,
  user: state.resetPassword.user,
  initialCodeExpiresAt: state.resetPassword.codeExpiresAt,
  initialCanResendAt: state.resetPassword.canResendAt,
});

export default connect(mapStateToProps, {
  matchCode,
  sendCodeToEmail,
  openResetLink,
})(EnterCode);
